import { ICardElementWithChildren, IWFState } from "backend/model/model";
import { getTranslatedAttribute, mea_t } from "qpr-base//tplutils/utils";

/**
 * KPI Description may include:
 * - <puser>value</puser> — primary data-entry owner; only this value (or <buser>)
 *   matches the logged-in user (SAML `meaLogonName` or `profile.email`) for KPI value tasks
 *   (unless admin / tenant admin skip).
 * - <buser>value</buser> — additional user who may see the same KPI task
 * - <pname>Display Name</pname> / <pusername>Display Name</pusername> — optional label shown in “Responsible”; does not replace
 *   email checks — use <puser> (or <buser>) for access control.
 * If neither <puser> nor <buser> is present, workflow lists behave as before (no extra filter).
 *
 * Newer KPI configs may store the same linkage in structured attributes / JS_ATTR:
 * { puser, pname, buser }. These are normalized into the same behavior below.
 */

export function normalizeWorkflowTagEmail(
  email: string | null | undefined
): string {
  if (email == null || typeof email !== "string") return "";
  return email.trim().toLowerCase();
}

/** Supports </puser> and mistaken duplicate opener <puser> as closer. */
const PUSER_REGEX = /<puser>\s*([^<]+?)\s*(?:<\/puser>|<puser>)/gi;
/** Supports </buser> and mistaken duplicate opener <buser> as closer. */
const BUSER_REGEX = /<buser>\s*([^<]+?)\s*(?:<\/buser>|<buser>)/gi;
/** Supports </pname> / </pusername> and mistaken duplicate opener as closer. */
const PNAME_REGEX = /<pname>\s*([^<]+?)\s*(?:<\/pname>|<pname>)/gi;
const PUSERNAME_REGEX =
  /<pusername>\s*([^<]+?)\s*(?:<\/pusername>|<pusername>)/gi;

type WorkflowUserAttributes = {
  pusers: string[];
  busers: string[];
  pnames: string[];
};

const emptyWorkflowUserAttributes = (): WorkflowUserAttributes => ({
  pusers: [],
  busers: [],
  pnames: [],
});

function normalizeWorkflowUserAttributeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function workflowUserAttributeKind(
  key: string
): "puser" | "buser" | "pname" | null {
  const normalized = normalizeWorkflowUserAttributeKey(key);
  if (
    normalized === "puser" ||
    normalized === "primaryuser" ||
    normalized === "championuser" ||
    normalized === "dataentryuser"
  ) {
    return "puser";
  }
  if (
    normalized === "buser" ||
    normalized === "backupuser" ||
    normalized === "businessuser" ||
    normalized === "secondaryuser"
  ) {
    return "buser";
  }
  if (
    normalized === "pname" ||
    normalized === "pusername" ||
    normalized === "primaryusername" ||
    normalized === "primaryname" ||
    normalized === "championname" ||
    normalized === "dataentryusername"
  ) {
    return "pname";
  }
  return null;
}

function addWorkflowUserAttributeValue(
  out: WorkflowUserAttributes,
  kind: "puser" | "buser" | "pname",
  value: unknown
): void {
  if (value == null) return;
  if (Array.isArray(value)) {
    value.forEach((v) => addWorkflowUserAttributeValue(out, kind, v));
    return;
  }
  if (typeof value === "object") {
    const maybe =
      (value as Record<string, unknown>).email ??
      (value as Record<string, unknown>).user ??
      (value as Record<string, unknown>).value ??
      (value as Record<string, unknown>).name;
    if (maybe != null) {
      addWorkflowUserAttributeValue(out, kind, maybe);
    }
    return;
  }
  const raw = String(value || "").trim();
  if (!raw) return;
  const values =
    kind === "buser"
      ? raw
          .split(/[;,]/)
          .map((v) => v.trim())
          .filter(Boolean)
      : [raw];
  if (kind === "puser") out.pusers.push(...values);
  if (kind === "buser") out.busers.push(...values);
  if (kind === "pname") out.pnames.push(...values);
}

function collectWorkflowUserAttributesFromUnknown(
  source: unknown,
  depth = 0,
  out: WorkflowUserAttributes = emptyWorkflowUserAttributes()
): WorkflowUserAttributes {
  if (source == null || depth > 8) return out;
  if (typeof source === "string") {
    const trimmed = source.trim();
    if (!trimmed || !/^[{[]/.test(trimmed)) return out;
    try {
      return collectWorkflowUserAttributesFromUnknown(
        JSON.parse(trimmed),
        depth + 1,
        out
      );
    } catch {
      return out;
    }
  }
  if (Array.isArray(source)) {
    source.forEach((v) =>
      collectWorkflowUserAttributesFromUnknown(v, depth + 1, out)
    );
    return out;
  }
  if (typeof source !== "object") return out;
  Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
    const kind = workflowUserAttributeKind(key);
    if (kind) {
      addWorkflowUserAttributeValue(out, kind, value);
    }
    if (value != null && typeof value === "object") {
      collectWorkflowUserAttributesFromUnknown(value, depth + 1, out);
    } else if (typeof value === "string" && /^[{[]/.test(value.trim())) {
      collectWorkflowUserAttributesFromUnknown(value, depth + 1, out);
    }
  });
  return out;
}

function workflowUserAttributesToTagText(attrs: WorkflowUserAttributes): string {
  return [
    ...attrs.pusers.map((v) => `<puser>${v}</puser>`),
    ...attrs.busers.map((v) => `<buser>${v}</buser>`),
    ...attrs.pnames.map((v) => `<pname>${v}</pname>`),
  ].join("\n");
}

function appendWorkflowUserAttributeParts(
  source: unknown,
  parts: string[]
): void {
  const text = workflowUserAttributesToTagText(
    collectWorkflowUserAttributesFromUnknown(source)
  );
  if (text) parts.push(text);
}

export function extractPusernameFromText(
  text: string | null | undefined
): string | null {
  if (!text || typeof text !== "string") return null;
  PNAME_REGEX.lastIndex = 0;
  const pname = PNAME_REGEX.exec(text);
  const pnameValue = pname?.[1]?.trim();
  if (pnameValue) return pnameValue;
  PUSERNAME_REGEX.lastIndex = 0;
  const m = PUSERNAME_REGEX.exec(text);
  const v = m?.[1]?.trim();
  return v || null;
}

export function extractPuserEmailFromText(
  text: string | null | undefined
): string | null {
  if (!text || typeof text !== "string") return null;
  PUSER_REGEX.lastIndex = 0;
  const m = PUSER_REGEX.exec(text);
  const v = m?.[1]?.trim();
  return v || null;
}

export function extractBuserEmailFromText(
  text: string | null | undefined
): string | null {
  if (!text || typeof text !== "string") return null;
  BUSER_REGEX.lastIndex = 0;
  const m = BUSER_REGEX.exec(text);
  const v = m?.[1]?.trim();
  return v || null;
}

export function extractBuserEmailsFromText(
  text: string | null | undefined
): string[] {
  if (!text || typeof text !== "string") return [];
  BUSER_REGEX.lastIndex = 0;
  const out: string[] = [];
  let m: RegExpExecArray | null = null;
  while ((m = BUSER_REGEX.exec(text)) !== null) {
    const v = m?.[1]?.trim();
    if (v) out.push(v);
  }
  return out;
}

/**
 * Removes workflow owner tags and their content from text shown in Methodology / description UI
 * (same idea as stripping &lt;k&gt;, &lt;t&gt;, etc.).
 */
export function stripWorkflowUserTagsFromText(
  text: string | null | undefined
): string {
  if (text == null || typeof text !== "string") return "";
  let s = text;
  for (let i = 0; i < 50; i += 1) {
    const before = s;
    s = s
      .replace(PUSER_REGEX, "")
      .replace(BUSER_REGEX, "")
      .replace(PNAME_REGEX, "")
      .replace(PUSERNAME_REGEX, "");
    if (s === before) break;
  }
  s = s
    .replace(/<\/?puser\s*>/gi, "")
    .replace(/<\/?buser\s*>/gi, "")
    .replace(/<\/?pname\s*>/gi, "")
    .replace(/<\/?pusername\s*>/gi, "");
  return s;
}

export function formatPuserEmailForDisplay(
  email: string | null | undefined,
  emailToDisplayName?: Record<string, string> | null
): string {
  if (!email?.trim()) return "";
  const key = normalizeWorkflowTagEmail(email);
  const resolved = key ? emailToDisplayName?.[key] : undefined;
  if (resolved?.trim()) return resolved.trim();
  return email.trim();
}

/**
 * Received From column: always prefer a person name; resolve email → name when possible.
 * Never leave a bare email when a display name is available in the map.
 */
export function formatReceivedFromForDisplay(
  receivedFrom: string | null | undefined,
  emailToDisplayName?: Record<string, string> | null
): string {
  const raw = String(receivedFrom || "").trim();
  if (!raw || raw === "-") return "-";
  if (!raw.includes("@")) return raw;
  const resolved = formatPuserEmailForDisplay(raw, emailToDisplayName);
  if (resolved && !resolved.includes("@")) return resolved;
  // While the name map is loading / missing, keep showing something (email) rather than "-".
  return resolved || raw;
}

/** Unique emails from workflowState.receivedfrom for display-name lookup. */
export function collectReceivedFromEmailsFromTasks(
  tasks: Array<{ workflowState?: IWFState }>
): string[] {
  const set = new Set<string>();
  for (const t of tasks) {
    const raw = String(t.workflowState?.receivedfrom || "").trim();
    if (!raw.includes("@")) continue;
    const k = normalizeWorkflowTagEmail(raw);
    if (k) set.add(k);
  }
  return Array.from(set);
}

function appendJsAttrDescriptionParts(
  element: ICardElementWithChildren,
  parts: string[]
): void {
  const js = (element as { JS_ATTR?: string }).JS_ATTR;
  if (!js || typeof js !== "string") return;
  try {
    const parsed = JSON.parse(js);
    appendWorkflowUserAttributeParts(parsed, parts);
    const loc = parsed?.["Localized-Text-Attributes"];
    if (loc && typeof loc === "object") {
      for (const key of Object.keys(loc)) {
        const d = loc[key]?.Description;
        if (typeof d === "string" && d) parts.push(d);
      }
    }
  } catch {
    // ignore
  }
  if (/puser|buser|pname|pusername/i.test(js)) parts.push(js);
}

/** Text sources for tags on a loaded KPI element (workflow input / MEA). */
export function getKpiTagSourceTextFromElement(
  element: ICardElementWithChildren,
  language: string
): string {
  const parts: string[] = [];
  const tr = getTranslatedAttribute(element, "Description", language);
  const raw = String(
    (element as { description?: string }).description ||
      (element as { Description?: string }).Description ||
      ""
  );
  if (tr) parts.push(tr);
  if (raw) parts.push(raw);
  appendWorkflowUserAttributeParts(element, parts);
  appendJsAttrDescriptionParts(element, parts);
  return parts.join("\n");
}

export function collectDescriptionsFromTaskMetadata(
  metadata: string | undefined | null
): string {
  if (!metadata || typeof metadata !== "string") return "";
  try {
    const parsed = JSON.parse(metadata);
    const attrsText = workflowUserAttributesToTagText(
      collectWorkflowUserAttributesFromUnknown(parsed)
    );
    const loc = parsed?.["Localized-Text-Attributes"];
    const descText =
      loc && typeof loc === "object"
        ? Object.keys(loc)
      .map((k) => {
        const block = loc[k];
        if (!block || typeof block !== "object") return "";
        const d =
          (block as { Description?: string }).Description ??
          (block as { description?: string }).description;
        return typeof d === "string" ? d : "";
      })
      .filter(Boolean)
            .join("\n")
        : "";
    return [descText, attrsText].filter(Boolean).join("\n");
  } catch {
    return "";
  }
}

/** Text sources for tags from workflow task metadata (mytasks / notifications). */
export function getKpiTagSourceTextFromWorkflowMetadata(
  metadata: string | undefined | null,
  language: string
): string {
  const parts: string[] = [];
  const fromLocales = collectDescriptionsFromTaskMetadata(metadata);
  if (fromLocales) parts.push(fromLocales);
  if (metadata) {
    const oneLang = mea_t("Description", metadata, language);
    if (
      oneLang &&
      oneLang !== "undefined" &&
      oneLang !== "missing metadata"
    ) {
      parts.push(oneLang);
    }
    const methodology = mea_t("Methodology", metadata, language);
    if (
      methodology &&
      methodology !== "undefined" &&
      methodology !== "missing metadata"
    ) {
      parts.push(methodology);
    }
    // Tags may live only in the raw JSON (unexpected keys, encoding) or outside
    // Localized-Text-Attributes — same idea as appendJsAttrDescriptionParts(JS_ATTR).
    if (/puser|buser|pname|pusername/i.test(metadata)) {
      parts.push(metadata);
    }
  }
  return parts.join("\n");
}

export function currentUserMatchesKpiPuserBuser(
  tagSourceText: string,
  userEmail: string | null | undefined,
  userLogonName?: string | null | undefined
): boolean {
  const p = extractPuserEmailFromText(tagSourceText);
  const busers = extractBuserEmailsFromText(tagSourceText);
  if (!p && busers.length === 0) return true;

  const uEmail = normalizeWorkflowTagEmail(userEmail);
  const uLogonName = normalizeWorkflowTagEmail(userLogonName);
  if (!uEmail && !uLogonName) return false;

  const pNorm = p ? normalizeWorkflowTagEmail(p) : "";
  const bNorms = busers
    .map((b) => normalizeWorkflowTagEmail(b))
    .filter(Boolean);

  const pm = pNorm ? pNorm === uEmail || pNorm === uLogonName : false;
  const bm = bNorms.some((bNorm) => bNorm === uEmail || bNorm === uLogonName);
  return pm || bm;
}

/**
 * Merges workflow task metadata with optional MEA-loaded KPI text (measureId → tags from model).
 * Task APIs often omit full Description in metadata; the map fills the gap for Task Workflow / My Tasks.
 */
export function mergeKpiTagSourcesForTask(
  metadata: string | null | undefined,
  language: string,
  measureId: string | undefined,
  tagTextByMeasureId?: Record<string, string> | null
): string {
  const fromWorkflow = getKpiTagSourceTextFromWorkflowMetadata(
    metadata,
    language
  );
  const fromElement =
    measureId && tagTextByMeasureId?.[measureId]
      ? tagTextByMeasureId[measureId]
      : "";
  return [fromWorkflow, fromElement].filter(Boolean).join("\n");
}

function isKpiValueDataEntryState(ws: IWFState): boolean {
  const shortState = String(ws.shortState || "").toLowerCase();
  return (
    shouldRestrictKpiTaskByPuserBuser(ws) &&
    (shortState === "dataentry" ||
      shortState === "approvalrejected" ||
      shortState === "update")
  );
}

function isKpiValueFinalApprovalState(ws: IWFState): boolean {
  if (!shouldRestrictKpiTaskByPuserBuser(ws)) return false;
  const shortState = String(ws.shortState || "").toLowerCase();
  const state = String(ws.state || "").toLowerCase();
  return (
    shortState === "finalapproval" ||
    state.includes("final approval") ||
    state.includes("finalapproval")
  );
}

/** Display name for KPI value workflows waiting at final approval. */
const KPI_VALUE_FINAL_APPROVAL_RESPONSIBLE = "Abdulrahman Alajlan";

/**
 * @param emailToDisplayName — lowercase email → full name (from /workflow/users/resolve-names-by-email)
 * @param tagTextByMeasureId — optional map from KPI measure id to tag source text (from MEA / getKpiTagSourceTextFromElement)
 */
export function getKpiResponsibleDisplayFromTask(
  ws: IWFState | undefined,
  language: string,
  emailToDisplayName?: Record<string, string> | null,
  tagTextByMeasureId?: Record<string, string> | null
): string {
  if (!ws) return "-";
  if (isKpiValueFinalApprovalState(ws)) {
    return KPI_VALUE_FINAL_APPROVAL_RESPONSIBLE;
  }
  if (!isKpiValueDataEntryState(ws)) return ws.responsible || "-";
  const text = mergeKpiTagSourcesForTask(
    ws.metadata,
    language,
    ws.measureId,
    tagTextByMeasureId
  );
  const displayName = extractPusernameFromText(text);
  if (displayName) return displayName;
  const p = extractPuserEmailFromText(text);
  if (p?.trim()) {
    return formatPuserEmailForDisplay(p, emailToDisplayName);
  }
  return ws.responsible || "-";
}

export function shouldRestrictKpiTaskByPuserBuser(
  workflowState: IWFState | undefined
): boolean {
  return (
    workflowState?.measureType === "KPI" &&
    workflowState?.wfShortType === "value"
  );
}

/**
 * When true, KPI value tasks are not filtered by puser/buser tags in the KPI description.
 * Data-entry owners stay restricted; first/final approvers must see all KPIs in their stage.
 */
export function shouldSkipKpiPuserBuserFilter(options: {
  isAdmin?: boolean;
  isTenantAdmin?: boolean;
  userWorkflowRole?: string | null | undefined;
}): boolean {
  if (options.isAdmin || options.isTenantAdmin) return true;
  const r = options.userWorkflowRole;
  return (
    r === "__WF_DATA_CHECKER" ||
    r === "__WF_DATA_APPROVE" ||
    r === "__WF_DATA_FINAL_APPROVE"
  );
}

export function workflowRowUsesKpiPuserBuserFilter(
  element: ICardElementWithChildren
): boolean {
  return (
    element?.typename === "KPI" &&
    element?.workflowInstance?.type === "value"
  );
}

export function filterTaskListByKpiPuserBuser<
  T extends { workflowState?: IWFState }
>(
  tasks: T[],
  language: string,
  userEmail: string | undefined | null,
  userLogonName: string | undefined | null,
  skip: boolean,
  tagTextByMeasureId?: Record<string, string> | null
): T[] {
  if (skip || !tasks?.length) return tasks;
  return tasks.filter((t) => {
    const ws = t.workflowState;
    if (!shouldRestrictKpiTaskByPuserBuser(ws)) return true;
    const text = mergeKpiTagSourcesForTask(
      ws?.metadata,
      language,
      ws?.measureId,
      tagTextByMeasureId
    );
    return currentUserMatchesKpiPuserBuser(text, userEmail, userLogonName);
  });
}

export function filterWorkflowInputRowsByKpiPuserBuser(
  rows: ICardElementWithChildren[],
  language: string,
  userEmail: string | undefined | null,
  userLogonName: string | undefined | null,
  skip: boolean
): ICardElementWithChildren[] {
  if (skip || !rows?.length) return rows;
  return rows.filter((row) => {
    if (!workflowRowUsesKpiPuserBuserFilter(row)) return true;
    const text = getKpiTagSourceTextFromElement(row, language);
    return currentUserMatchesKpiPuserBuser(text, userEmail, userLogonName);
  });
}

/** Unique lowercase emails from <puser> on KPI value tasks (for name resolution). */
export function collectNormalizedPuserEmailsFromTasks(
  tasks: Array<{ workflowState?: IWFState }>,
  language: string,
  tagTextByMeasureId?: Record<string, string> | null
): string[] {
  const set = new Set<string>();
  for (const t of tasks) {
    const ws = t.workflowState;
    if (!shouldRestrictKpiTaskByPuserBuser(ws)) continue;
    const text = mergeKpiTagSourcesForTask(
      ws?.metadata,
      language,
      ws?.measureId,
      tagTextByMeasureId
    );
    const e = extractPuserEmailFromText(text);
    const k = normalizeWorkflowTagEmail(e);
    if (k) set.add(k);
  }
  return Array.from(set);
}

export function collectNormalizedPuserEmailsFromWorkflowRows(
  rows: ICardElementWithChildren[],
  language: string
): string[] {
  const set = new Set<string>();
  for (const row of rows) {
    if (!workflowRowUsesKpiPuserBuserFilter(row)) continue;
    const text = getKpiTagSourceTextFromElement(row, language);
    const e = extractPuserEmailFromText(text);
    const k = normalizeWorkflowTagEmail(e);
    if (k) set.add(k);
  }
  return Array.from(set);
}
